import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Calendar, Video, Clock, ExternalLink, Loader2, LogOut, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentClasses = () => {
  const { settings } = usePublicSettings();
  const cancellationWindow = settings?.cancellation_window_hours ?? 24;
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, completed
  const [unenrolling, setUnenrolling] = useState(null);
  const isUpcomingStatus = (status) => ['scheduled', 'full'].includes(status);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/classes?page_size=200`);
      setClasses(response.data.data ?? response.data);
    } catch (err) {
      console.error('Failed to fetch classes:', err);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const isWithinWindow = (scheduledAt) => {
    if (!scheduledAt) return false;
    const hoursUntil = (new Date(scheduledAt) - Date.now()) / (1000 * 60 * 60);
    return hoursUntil < cancellationWindow && hoursUntil > 0;
  };

  const handleUnenroll = async (classId, scheduledAt) => {
    if (cancellationWindow > 0 && isWithinWindow(scheduledAt)) {
      toast.error(
        `Cancellation is not allowed within ${cancellationWindow}h of a scheduled class. Please contact your teacher directly.`,
        { duration: 6000 }
      );
      return;
    }
    setUnenrolling(classId);
    try {
      await axios.delete(`${API_URL}/api/student/classes/${classId}/enroll`);
      toast.success('Successfully cancelled class');
      setClasses(prev => prev.filter(c => c.id !== classId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setUnenrolling(null);
    }
  };

  const filteredClasses = classes.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'upcoming') return isUpcomingStatus(c.status);
    if (filter === 'completed') return c.status === 'completed';
    return true;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-classes-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          My Classes
        </h1>
        <p className="text-[#1A2E6E]/60 mt-1">
          View your scheduled and past classes
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white rounded-xl p-1.5 border border-[#E8EAF0] w-fit">
        {[
          { id: 'all', label: 'All' },
          { id: 'upcoming', label: 'Upcoming' },
          { id: 'completed', label: 'Completed' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === tab.id
                ? 'bg-[#1A2E6E] text-white'
                : 'text-[#1A2E6E]/70 hover:text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Classes List */}
      {filteredClasses.length > 0 ? (
        <div className="space-y-4">
          {filteredClasses.map((classItem, index) => (
            <motion.div
              key={classItem.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Icon */}
                  <div className={`
                    w-14 h-14 rounded-xl flex items-center justify-center shrink-0
                    ${isUpcomingStatus(classItem.status)
                      ? 'bg-[#4CAF50]/10' 
                      : classItem.status === 'completed'
                        ? 'bg-[#1A2E6E]/10'
                        : 'bg-[#D72B2B]/10'
                    }
                  `}>
                    <Video className={`w-7 h-7 ${
                      isUpcomingStatus(classItem.status)
                        ? 'text-[#4CAF50]'
                        : classItem.status === 'completed'
                          ? 'text-[#1A2E6E]'
                          : 'text-[#D72B2B]'
                    }`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-semibold text-[#1A2E6E] text-lg">
                        {classItem.title}
                      </h3>
                      <span className={`
                        px-3 py-1 rounded-full text-xs font-medium shrink-0
                        ${isUpcomingStatus(classItem.status)
                          ? 'bg-[#4CAF50]/10 text-[#4CAF50]' 
                          : classItem.status === 'completed'
                            ? 'bg-[#1A2E6E]/10 text-[#1A2E6E]'
                            : 'bg-[#D72B2B]/10 text-[#D72B2B]'
                        }
                      `}>
                        {classItem.status === 'scheduled' ? 'Scheduled' :
                         classItem.status === 'full' ? 'Full' :
                         classItem.status === 'completed' ? 'Completed' : 'Cancelled'}
                      </span>
                    </div>

                    {classItem.description && (
                      <p className="text-sm text-[#1A2E6E]/60 mb-3">
                        {classItem.description}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#1A2E6E]/70">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {formatDate(classItem.scheduled_at)}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {formatTime(classItem.scheduled_at)} ({classItem.duration_minutes}min)
                      </span>
                    </div>

                    {classItem.status === 'cancelled' && (
                      <p className="mt-3 text-sm text-red-600">
                        This class was cancelled and is no longer available to join.
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 shrink-0">
                    {isUpcomingStatus(classItem.status) && classItem.meeting_link && (
                      <a
                        href={classItem.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-2.5 bg-[#4CAF50] text-white rounded-xl hover:bg-[#388E3C] transition-colors font-medium"
                      >
                        Join Class <ExternalLink size={16} />
                      </a>
                    )}
                    {isUpcomingStatus(classItem.status) && (() => {
                      const locked = cancellationWindow > 0 && isWithinWindow(classItem.scheduled_at);
                      const label = classItem.enrollment_type === 'open' ? 'Unenroll' : 'Cancel';
                      return (
                        <button
                          onClick={() => handleUnenroll(classItem.id, classItem.scheduled_at)}
                          disabled={unenrolling === classItem.id || locked}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors font-medium disabled:opacity-50 ${
                            locked
                              ? 'border border-amber-200 text-amber-700 bg-amber-50 cursor-not-allowed'
                              : 'border border-red-200 text-red-600 hover:bg-red-50'
                          }`}
                          title={locked ? `Cannot cancel within ${cancellationWindow}h of class — contact your teacher` : `${label} from this class`}
                        >
                          {unenrolling === classItem.id
                            ? <Loader2 size={16} className="animate-spin" />
                            : locked
                              ? <AlertTriangle size={16} />
                              : <LogOut size={16} />
                          }
                          {locked ? `${cancellationWindow}h Policy` : label}
                        </button>
                      );
                    })()}
                  </div>
                </div>

                {/* Notes (if any) */}
                {classItem.notes && (
                  <div className="mt-4 pt-4 border-t border-[#E8EAF0]">
                    <p className="text-sm text-[#1A2E6E]/70">
                      <span className="font-medium text-[#1A2E6E]">Notes:</span> {classItem.notes}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <Calendar className="w-16 h-16 text-[#1A2E6E]/20 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#1A2E6E] mb-2">No Classes Found</h3>
          <p className="text-[#1A2E6E]/60 max-w-md mx-auto">
            {filter === 'upcoming' 
              ? "You don't have any upcoming classes scheduled."
              : filter === 'completed'
                ? "You haven't completed any classes yet."
                : "No classes have been scheduled for you yet."
            }
          </p>
          {(filter === 'all' || filter === 'upcoming') && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/student/schedule"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A2E6E] text-white rounded-xl text-sm font-medium hover:bg-[#16275C] transition-colors"
              >
                Schedule 1:1
              </Link>
              <Link
                to="/student/available"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl text-sm font-medium hover:bg-[#F5F7FA] transition-colors"
              >
                View open classes
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentClasses;
